<template>
   <!-- Live Feeds widget: Live Stats / Social Network / Revenue (sample data for now) -->
   <div class="card bg-sa-card live-feeds mb-4">
      <!-- Header: title on the left, tabs on the right -->
      <div class="card-header bg-sa-card-header lf-header">
         <span class="lf-title"><i class="fa fa-bar-chart me-2"></i>Live Feeds</span>
         <ul class="nav lf-tabs">
            <li v-for="tab in tabs" :key="tab.key" class="nav-item">
               <button type="button" class="lf-tab" :class="{ active: activeTab === tab.key }" @click="activeTab = tab.key">
                  <i :class="tab.icon"></i><span class="d-none d-sm-inline ms-1">{{ tab.label }}</span>
               </button>
            </li>
         </ul>
      </div>

      <div ref="body" class="card-body bg-sa-card-body p-0">
         <!-- ================= LIVE STATS ================= -->
         <div v-if="activeTab === 'live'">
            <div class="row g-0">
               <div class="col-lg-8 lf-live-chart">
                  <!-- Live switch: ON = new value every second -->
                  <label class="lf-switch">
                     <span class="fw-semibold me-2">Live switch</span>
                     <input v-model="liveOn" type="checkbox" class="d-none" @change="toggleLive" />
                     <span class="lf-switch-box" :class="{ on: liveOn }">
                        <i class="fa" :class="liveOn ? 'fa-check' : 'fa-times'"></i>{{ liveOn ? "ON" : "OFF" }}
                     </span>
                  </label>
                  <apexchart type="area" height="240" :options="liveOptions" :series="liveSeries" />
               </div>

               <!-- Progress bars + buttons -->
               <div class="col-lg-4 lf-side">
                  <div v-for="item in progressItems" :key="item.label" class="lf-progress">
                     <div class="d-flex justify-content-between">
                        <span>{{ item.label }}</span>
                        <span>{{ item.value }}</span>
                     </div>
                     <div class="lf-progress-track">
                        <!-- width comes from the data, so it has to be set here -->
                        <div class="lf-progress-bar" :class="item.colorClass" :style="{ width: item.percent + '%' }"></div>
                     </div>
                  </div>
                  <div class="d-flex gap-2 mt-3">
                     <button type="button" class="btn btn-outline-secondary flex-fill" @click="$emit('generate-pdf')">Generate PDF</button>
                     <button type="button" class="btn btn-outline-secondary flex-fill" @click="$emit('report-bug')">Report a bug</button>
                  </div>
               </div>
            </div>

            <!-- Rings at the bottom -->
            <div class="row g-0 lf-rings">
               <div v-for="ring in rings" :key="ring.label" class="col-6 col-md-3 lf-ring-item">
                  <svg viewBox="0 0 36 36" class="lf-ring">
                     <circle class="lf-ring-track" cx="18" cy="18" r="15.9155" />
                     <circle class="lf-ring-value" :class="ring.colorClass" cx="18" cy="18" r="15.9155" :stroke-dasharray="ring.percent + ' 100'" />
                     <text x="18" y="20.5" class="lf-ring-text">{{ ring.text }}</text>
                  </svg>
                  <span class="lf-ring-label">
                     {{ ring.label }}
                     <i class="fa" :class="ring.up ? 'fa-caret-up text-danger' : 'fa-caret-down text-success'"></i>
                  </span>
                  <span class="lf-ring-badges">
                     <span class="lf-badge" :class="ring.badge1Class">{{ ring.badge1 }}</span>
                     <span class="lf-badge" :class="ring.badge2Class">{{ ring.badge2 }}</span>
                  </span>
               </div>
            </div>
         </div>

         <!-- ================= SOCIAL NETWORK ================= -->
         <div v-if="activeTab === 'social'">
            <div class="lf-toolbar">
               <input v-model="socialFrom" type="date" class="form-control form-control-sm lf-date" title="Show From" />
               <input v-model="socialTo" type="date" class="form-control form-control-sm lf-date" title="To" />
               <div class="dropdown ms-auto">
                  <button type="button" class="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                     <i class="fa fa-cog me-1"></i>More
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end">
                     <li><button type="button" class="dropdown-item" @click="downloadPng('socialChart', 'social-network')"><i class="fa fa-image fa-fw me-1"></i>Download image</button></li>
                     <li><button type="button" class="dropdown-item" @click="downloadSocialCsv"><i class="fa fa-file-text-o fa-fw me-1"></i>Download CSV</button></li>
                     <li><hr class="dropdown-divider" /></li>
                     <li><button type="button" class="dropdown-item" @click="socialFrom = ''; socialTo = ''"><i class="fa fa-undo fa-fw me-1"></i>Show all dates</button></li>
                  </ul>
               </div>
            </div>
            <div class="lf-chart">
               <apexchart ref="socialChart" type="area" height="260" :options="socialOptions" :series="socialSeries" />
            </div>
         </div>

         <!-- ================= REVENUE ================= -->
         <div v-if="activeTab === 'revenue'">
            <div class="lf-toolbar">
               <label v-for="name in revenueNames" :key="name" class="lf-check">
                  <input type="checkbox" :checked="revenueShown[name]" @change="toggleRevenueSeries(name, $event.target.checked)" />
                  <span>{{ name }}</span>
               </label>
               <div class="dropdown ms-auto">
                  <button type="button" class="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                     <i class="fa fa-cog me-1"></i>More
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end">
                     <li><button type="button" class="dropdown-item" @click="downloadPng('revenueChart', 'revenue')"><i class="fa fa-image fa-fw me-1"></i>Download image</button></li>
                     <li><button type="button" class="dropdown-item" @click="downloadRevenueCsv"><i class="fa fa-file-text-o fa-fw me-1"></i>Download CSV</button></li>
                  </ul>
               </div>
            </div>
            <div class="lf-chart">
               <apexchart ref="revenueChart" type="line" height="270" :options="revenueOptions" :series="revenueSeries" />
            </div>
         </div>
      </div>
   </div>
</template>

<script>
export default {
   name: "LiveFeeds",
   emits: ["generate-pdf", "report-bug"],
   data() {
      return {
         activeTab: "live",
         tabs: [
            { key: "live", label: "Live Stats", icon: "fa fa-clock-o" },
            { key: "social", label: "Social Network", icon: "fa fa-facebook" },
            { key: "revenue", label: "Revenue", icon: "fa fa-dollar" },
         ],

         // Colours of chart text/lines: read from the skin (see readSkinColors)
         textColor: "#888",
         darkSkin: false,
         themeObserver: null,

         /* ---------- Live Stats (sample) ---------- */
         liveOn: false,
         liveTimer: null,
         liveValues: this.makeLiveValues(),
         progressItems: [
            { label: "My Tasks", value: "130/200", percent: 65, colorClass: "lf-c-dark" },
            { label: "Transfered", value: "440 GB", percent: 34, colorClass: "lf-c-steel" },
            { label: "Bugs Squashed", value: "77%", percent: 77, colorClass: "lf-c-steel" },
            { label: "User Testing", value: "7 Days", percent: 84, colorClass: "lf-c-olive" },
         ],
         rings: [
            { label: "SERVER LOAD", text: "33 %", percent: 33, colorClass: "lf-c-brown", up: true, badge1: "▲ 97%", badge1Class: "lf-b-green", badge2: "▼ 44%", badge2Class: "lf-b-blue" },
            { label: "DISK SPACE", text: "79 %", percent: 79, colorClass: "lf-c-olive", up: false, badge1: "▲ 76%", badge1Class: "lf-b-grey", badge2: "▼ 3%", badge2Class: "lf-b-blue" },
            { label: "TRANSFERED", text: "23 %", percent: 23, colorClass: "lf-c-steel", up: true, badge1: "10GB", badge1Class: "lf-b-grey", badge2: "▲ 10%", badge2Class: "lf-b-grey" },
            { label: "TEMPERATURE", text: "36°F", percent: 36, colorClass: "lf-c-dark", up: false, badge1: "▲ 124", badge1Class: "lf-b-pink", badge2: "▼ 40 F", badge2Class: "lf-b-blue" },
         ],

         /* ---------- Social Network (sample, one point per month) ---------- */
         socialFrom: "",
         socialTo: "",
         socialData: [
            { date: "2025-10-01", a: 27, b: 25 }, { date: "2025-11-01", a: 34, b: 31 },
            { date: "2025-12-01", a: 51, b: 45 }, { date: "2026-01-01", a: 48, b: 37 },
            { date: "2026-02-01", a: 55, b: 38 }, { date: "2026-03-01", a: 65, b: 40 },
            { date: "2026-04-01", a: 61, b: 47 }, { date: "2026-05-01", a: 70, b: 55 },
            { date: "2026-06-01", a: 65, b: 43 }, { date: "2026-07-01", a: 75, b: 50 },
            { date: "2026-08-01", a: 57, b: 47 }, { date: "2026-09-01", a: 59, b: 39 },
            { date: "2026-10-01", a: 62, b: 47 },
         ],

         /* ---------- Revenue (sample, one point per quarter) ---------- */
         revenueNames: ["Target", "Actual", "Signups"],
         revenueShown: { Target: true, Actual: true, Signups: true },
         revenueLabels: ["Jan 2023", "Apr 2023", "Jul 2023", "Oct 2023", "Jan 2024", "Apr 2024", "Jul 2024", "Oct 2024", "Jan 2025", "Apr 2025", "Jul 2025", "Oct 2025"],
         revenueSeries: [
            { name: "Target", type: "column", data: [170, 670, 210, 680, 810, 1090, 370, 760, 540, 270, 700, 380] },
            { name: "Actual", type: "line", data: [60, 75, 110, 95, 980, 810, 730, 690, 30, 90, 100, 40] },
            { name: "Signups", type: "line", data: [660, 440, 790, 360, 500, 470, 490, 250, 850, 670, 250, 350] },
         ],
      };
   },
   computed: {
      gridColor() {
         return "rgba(128,128,128,0.18)";
      },
      // Shared look for all charts (follows the skin)
      chartBase() {
         return {
            toolbar: { show: false },
            zoom: { enabled: false },
            foreColor: this.textColor,
            background: "transparent",
            fontFamily: "inherit",
         };
      },

      /* ---------- Live Stats ---------- */
      liveSeries() {
         return [{ name: "Load", data: this.liveValues.map((y, x) => [x, y]) }];
      },
      liveOptions() {
         return {
            chart: { ...this.chartBase, type: "area", animations: { enabled: false } },
            colors: ["#6c8fa9"],
            stroke: { width: 1.5, curve: "straight" },
            fill: { type: "solid", opacity: 0.3 },
            dataLabels: { enabled: false },
            legend: { show: false },
            tooltip: { enabled: false },
            grid: { borderColor: this.gridColor, xaxis: { lines: { show: true } } },
            xaxis: { type: "numeric", min: 0, max: 100, tickAmount: 10, labels: { formatter: (v) => Math.round(v) } },
            yaxis: { min: 0, max: 100, tickAmount: 5 },
         };
      },

      /* ---------- Social Network ---------- */
      socialFiltered() {
         return this.socialData.filter((p) =>
            (!this.socialFrom || p.date >= this.socialFrom) && (!this.socialTo || p.date <= this.socialTo));
      },
      socialSeries() {
         return [
            { name: "Followers", data: this.socialFiltered.map((p) => p.a) },
            { name: "Engaged", data: this.socialFiltered.map((p) => p.b) },
         ];
      },
      socialOptions() {
         return {
            chart: { ...this.chartBase, type: "area" },
            colors: ["#568a89", "#3276b1"],
            stroke: { width: 1.5, curve: "straight" },
            fill: { type: "solid", opacity: [0.12, 0.18] },
            markers: { size: 4, colors: ["#fff"], strokeColors: ["#568a89", "#3276b1"], strokeWidth: 2 },
            dataLabels: { enabled: false },
            legend: { show: false },
            tooltip: { theme: this.darkSkin ? "dark" : "light" },
            grid: { borderColor: this.gridColor, xaxis: { lines: { show: true } } },
            xaxis: {
               categories: this.socialFiltered.map((p) => this.monthLabel(p.date)),
               labels: { show: false },
               axisTicks: { show: false },
               axisBorder: { show: false },
            },
            yaxis: { min: 0, max: 80, tickAmount: 4 },
         };
      },

      /* ---------- Revenue ---------- */
      revenueOptions() {
         return {
            chart: { ...this.chartBase, type: "line" },
            colors: ["#a90329", "#3276b1", "#71843f"],
            stroke: { width: [0, 3, 1.5], curve: "straight" },
            fill: {
               type: ["gradient", "solid", "solid"],
               gradient: { shade: "light", type: "vertical", shadeIntensity: 0.35, opacityFrom: 0.75, opacityTo: 1, stops: [0, 100] },
            },
            plotOptions: { bar: { columnWidth: "45%" } },
            markers: { size: [0, 4, 4], colors: ["#fff"], strokeColors: ["#a90329", "#3276b1", "#71843f"], strokeWidth: 2 },
            dataLabels: { enabled: false },
            legend: { show: false },   // the Target / Actual / Signups ticks replace it
            tooltip: { shared: true, intersect: false, theme: this.darkSkin ? "dark" : "light" },
            grid: { borderColor: this.gridColor, xaxis: { lines: { show: true } } },
            xaxis: { categories: this.revenueLabels },
            yaxis: { min: 0, max: 1250, tickAmount: 5 },
         };
      },
   },
   mounted() {
      this.readSkinColors();
      // Changing skin: read the colours again
      this.themeObserver = new MutationObserver(() => this.$nextTick(this.readSkinColors));
      this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
   },
   beforeUnmount() {
      clearInterval(this.liveTimer);
      if (this.themeObserver) this.themeObserver.disconnect();
   },
   methods: {
      // Chart text colour = the card's text colour; light text means a dark skin
      readSkinColors() {
         const el = this.$refs.body || this.$el;
         if (!el) return;
         const color = getComputedStyle(el).color;
         this.textColor = color;
         const [r, g, b] = (color.match(/\d+/g) || [128, 128, 128]).map(Number);
         this.darkSkin = (r * 299 + g * 587 + b * 114) / 1000 > 150;
      },
      // 101 sample values that wander between about 30 and 60
      makeLiveValues() {
         const values = [];
         let v = 48;
         for (let i = 0; i <= 100; i++) {
            v = Math.min(65, Math.max(30, v + (Math.random() - 0.5) * 6));
            values.push(Math.round(v));
         }
         return values;
      },
      // Live switch ON: drop the oldest value and add a new one every second
      toggleLive() {
         clearInterval(this.liveTimer);
         if (!this.liveOn) return;
         this.liveTimer = setInterval(() => {
            const last = this.liveValues[this.liveValues.length - 1];
            const next = Math.round(Math.min(65, Math.max(30, last + (Math.random() - 0.5) * 6)));
            this.liveValues = [...this.liveValues.slice(1), next];
         }, 1000);
      },
      monthLabel(iso) {
         return new Date(iso + "T00:00:00").toLocaleDateString([], { month: "short", year: "numeric" });
      },
      // Target / Actual / Signups ticks
      toggleRevenueSeries(name, show) {
         this.revenueShown[name] = show;
         const comp = this.$refs.revenueChart;
         if (!comp) return;
         const action = show ? "showSeries" : "hideSeries";
         if (typeof comp[action] === "function") comp[action](name);
         else if (comp.chart) comp.chart[action](name);
      },
      // More → Download image
      downloadPng(refName, fileName) {
         const comp = this.$refs[refName];
         const chart = comp && (comp.chart || comp);
         if (!chart || typeof chart.dataURI !== "function") return;
         chart.dataURI().then(({ imgURI }) => this.saveFile(imgURI, fileName + ".png"));
      },
      downloadRevenueCsv() {
         const rows = [["Quarter", ...this.revenueNames]];
         this.revenueLabels.forEach((label, i) => rows.push([label, ...this.revenueSeries.map((s) => s.data[i])]));
         this.saveCsv(rows, "revenue.csv");
      },
      downloadSocialCsv() {
         const rows = [["Month", "Followers", "Engaged"]];
         this.socialFiltered.forEach((p) => rows.push([this.monthLabel(p.date), p.a, p.b]));
         this.saveCsv(rows, "social-network.csv");
      },
      saveCsv(rows, fileName) {
         const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
         const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
         this.saveFile(url, fileName);
         setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      saveFile(href, fileName) {
         const a = document.createElement("a");
         a.href = href;
         a.download = fileName;
         document.body.appendChild(a);
         a.click();
         a.remove();
      },
   },
};
</script>

<style scoped>
/* ---------- Header + tabs ---------- */
.lf-header {
   display: flex;
   align-items: stretch;
   justify-content: space-between;
   padding-top: 0;
   padding-bottom: 0;
}
.lf-title {
   display: flex;
   align-items: center;
   padding: 0.6rem 0;
}
.lf-tabs {
   flex-wrap: nowrap;
   margin-right: calc(-1 * var(--bs-card-cap-padding-x, 1rem));
}
.lf-tab {
   height: 100%;
   padding: 0.6rem 1rem;
   background: transparent;
   border: 0;
   border-left: 1px solid var(--sa-border, rgba(128, 128, 128, 0.3));
   border-top: 3px solid transparent;
   color: inherit;
   opacity: 0.75;
   white-space: nowrap;
}
.lf-tab:hover {
   opacity: 1;
}
.lf-tab.active {
   opacity: 1;
   font-weight: 600;
   border-top-color: var(--sa-accent, #3276b1);
   background: var(--table-row-bg-hover, rgba(128, 128, 128, 0.12));
}

/* ---------- Toolbars (ticks, dates, More) ---------- */
.lf-toolbar {
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 0.5rem 1.25rem;
   padding: 0.6rem 0.75rem;
   border-bottom: 1px solid var(--sa-border, rgba(128, 128, 128, 0.3));
}
.lf-date {
   width: 10.5rem;
}
.lf-check {
   display: inline-flex;
   align-items: center;
   gap: 0.4rem;
   cursor: pointer;
   margin: 0;
}
.lf-check input {
   width: 1.15rem;
   height: 1.15rem;
   accent-color: var(--sa-accent, #3276b1);
}
.lf-chart {
   padding: 0.5rem 0.5rem 0;
}

/* ---------- Live Stats ---------- */
.lf-live-chart {
   position: relative;
   padding: 0.5rem;
}
.lf-switch {
   position: absolute;
   top: 1rem;
   left: 3.5rem;
   z-index: 2;
   display: inline-flex;
   align-items: center;
   font-size: 0.8rem;
   cursor: pointer;
}
.lf-switch-box {
   display: inline-flex;
   align-items: center;
   gap: 0.35rem;
   padding: 0.05rem 0.5rem 0.05rem 0.15rem;
   border: 1px solid rgba(128, 128, 128, 0.5);
   border-radius: 1rem;
   font-size: 0.7rem;
   font-weight: 600;
}
.lf-switch-box i {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 1.1rem;
   height: 1.1rem;
   border-radius: 50%;
   border: 1px solid rgba(128, 128, 128, 0.5);
   color: #dc3545;
   font-size: 0.65rem;
}
.lf-switch-box.on i {
   color: #198754;
}
.lf-side {
   padding: 0.75rem;
}
.lf-progress {
   margin-bottom: 0.9rem;
}
.lf-progress-track {
   height: 0.5rem;
   margin-top: 0.25rem;
   background: rgba(128, 128, 128, 0.22);
}
.lf-progress-bar {
   height: 100%;
}

/* Rings */
.lf-rings {
   border-top: 1px solid var(--sa-border, rgba(128, 128, 128, 0.3));
}
.lf-ring-item {
   display: flex;
   align-items: center;
   gap: 0.6rem;
   padding: 0.6rem 0.75rem;
   border-right: 1px solid var(--sa-border, rgba(128, 128, 128, 0.3));
}
.lf-ring {
   width: 3.4rem;
   height: 3.4rem;
   flex-shrink: 0;
   transform: rotate(-90deg);
}
.lf-ring-track,
.lf-ring-value {
   fill: none;
   stroke-width: 3;
}
.lf-ring-track {
   stroke: rgba(128, 128, 128, 0.2);
}
.lf-ring-value {
   stroke-linecap: butt;
}
.lf-ring-text {
   transform: rotate(90deg);
   transform-origin: 18px 18px;
   text-anchor: middle;
   font-size: 0.5rem;
   font-weight: 600;
   fill: currentColor;
}
.lf-ring-label {
   flex: 1 1 auto;
   font-size: 0.8rem;
   font-weight: 600;
   min-width: 0;
}
.lf-ring-badges {
   display: flex;
   flex-direction: column;
   gap: 0.2rem;
   flex-shrink: 0;
}
.lf-badge {
   padding: 0.05rem 0.35rem;
   border-radius: 2px;
   font-size: 0.65rem;
   font-weight: 700;
   color: #fff;
   text-align: center;
}

/* Colours (same for every skin, like the original) */
.lf-c-dark { background: #4c4f53; stroke: #4c4f53; }
.lf-c-steel { background: #57889c; stroke: #57889c; }
.lf-c-olive { background: #71843f; stroke: #71843f; }
.lf-c-brown { background: #b09b5b; stroke: #b09b5b; }
.lf-ring-value.lf-c-dark,
.lf-ring-value.lf-c-steel,
.lf-ring-value.lf-c-olive,
.lf-ring-value.lf-c-brown { background: none; }
.lf-b-green { background: #a8b98b; }
.lf-b-blue { background: #a5bdcf; }
.lf-b-grey { background: #999; }
.lf-b-pink { background: #d6879a; }

/* Phones: rings two per row, the switch sits higher */
@media (max-width: 767.98px) {
   .lf-ring-item:nth-child(2n) { border-right: 0; }
   .lf-ring-item:nth-child(-n + 2) { border-bottom: 1px solid var(--sa-border, rgba(128, 128, 128, 0.3)); }
   .lf-switch { left: 1rem; top: 0.5rem; }
}
</style>